import { fmtBytes, type UploadProgress } from '@/lib/uploadLimits';
import { Icon } from './Icon';

interface Props {
  /** Postęp 0–100. */
  percent: number;
  /** Bajty wysłane (opcjonalne — pokazuje „X / Y"). */
  loadedBytes?: number;
  /** Rozmiar całości (opcjonalne). */
  totalBytes?: number;
  /** Etykieta nad paskiem (np. „Wysyłanie pliku…"). */
  label?: string;
  /** Gdy podane — pokazuje przycisk „Anuluj" (aktywny dopóki percent < 100). */
  onAbort?: () => void;
  /** Kompaktowy wariant (cieńszy pasek, mniejszy tekst) do kart / wierszy list. */
  compact?: boolean;
  className?: string;
}

/**
 * Wspólny pasek postępu uploadu. Używany wszędzie, gdzie użytkownik dodaje pliki,
 * aby wysyłka dużych plików (do 100 MB) miała czytelny feedback zamiast „zawieszenia".
 */
export function UploadProgressBar({
  percent, loadedBytes, totalBytes, label, onAbort, compact, className,
}: Props) {
  const pct = Math.max(0, Math.min(100, Math.round(percent)));
  const done = pct >= 100;
  return (
    <div className={(className ?? '') + ' w-full'}>
      {label && (
        <div className={'text-ink-700 font-semibold mb-1 ' + (compact ? 'text-[11px]' : 'text-[13px]')}>
          {label}
        </div>
      )}
      <div className={'w-full bg-ink-100 rounded-full overflow-hidden ' + (compact ? 'h-1.5' : 'h-2.5')}>
        <div className="bg-water-500 h-full transition-all" style={{ width: pct + '%' }} />
      </div>
      <div className={'mt-1 flex items-center gap-2 text-ink-500 font-mono ' + (compact ? 'text-[10px]' : 'text-[12px]')}>
        <span>{pct}%</span>
        {loadedBytes != null && totalBytes != null && totalBytes > 0 && (
          <span>{fmtBytes(loadedBytes)} / {fmtBytes(totalBytes)}</span>
        )}
        {done && <span className="text-ink-400">Przetwarzanie po stronie serwera…</span>}
        {onAbort && !done && (
          <button
            type="button"
            onClick={onAbort}
            className="ml-auto text-red-600 hover:text-red-700 inline-flex items-center gap-1">
            <Icon name="x" className="w-3 h-3" /> Anuluj
          </button>
        )}
      </div>
    </div>
  );
}

/** Re-eksport typu dla wygody importu z jednego miejsca. */
export type { UploadProgress };
